'use client'

import Link from 'next/link'
import GfBento, { bucketCount } from '@/components/common/GfBento'
import { getLocalizedContent } from '@/utils/multilingual'
import { getCategoryTitle } from '@/utils/categoryHelpers'
import { getImageUrl, getImageAlt } from '@/utils/blog/imageHelpers'
import { formatDateSafe } from '@/utils/dateHelpers'

// Stable string hash → lets us shuffle deterministically (same order on the
// server and on the client, so no hydration mismatch) instead of by date.
const hashId = (s = '') => {
    let h = 0
    for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0
    return h >>> 0
}

/**
 * "Gallery · Field" article grid (.gf-arts → .ar cards).
 * Renders raw Sanity blog posts; reused on service + country pages.
 */
export default function GfArticleCards({ posts = [], lang = 'en', limit = 6, variant = 'grid', bucket = false, showExcerpt = true }) {
    const mapped = (posts || [])
        .map((post) => {
            const content = getLocalizedContent(post, lang)
            if (!content?.title) return null
            const primaryCategory = post.categories?.[0] || post.category
            // Category title across shapes: titleRu/titleEn (blog query) or a flat
            // `title` string (country query) or fallback.
            const cat = getCategoryTitle(primaryCategory, lang)
                || (typeof primaryCategory?.title === 'string' ? primaryCategory.title : '')
                || (lang === 'ru' ? 'Статья' : 'Article')
            return {
                id: post._id,
                href: `/${lang}/blog/${post.slug?.current}`,
                title: content.title,
                excerpt: showExcerpt ? content.description : '',
                date: post.publishedAt ? formatDateSafe(post.publishedAt, lang) : '',
                cat,
                img: getImageUrl(post.mainImage, { width: 760, height: 480 }),
                alt: getImageAlt(post.mainImage) || content.title,
            }
        })
        .filter(Boolean)

    // Mosaic: shuffle deterministically (by id hash) so the mix isn't date-sorted.
    const ordered = variant === 'mosaic'
        ? mapped.slice().sort((a, b) => hashId(a.id) - hashId(b.id))
        : mapped

    // `bucket` (teaser sections) snaps the shown count to 3/6/9 (see bucketCount);
    // full blog listings opt out and show everything via `limit`.
    const max = bucket
        ? bucketCount(ordered.length)
        : variant === 'mosaic' ? Math.max(limit, 9) : limit
    const items = ordered.slice(0, max)

    if (items.length === 0) return null

    const readLabel = lang === 'ru' ? 'Читать' : 'Read'
    const Card = ({ it, feat }) => (
        <Link key={it.id} className={`ar ${feat ? 'feat' : ''}`} href={it.href}>
            <div className="ph">
                {it.img && <img src={it.img} alt={it.alt} loading="lazy" />}
                <span className="tag">{it.cat}</span>
            </div>
            <div className="ar-body">
                <span className="date">{it.date}</span>
                <h3>{it.title}</h3>
                {it.excerpt && <p className="ex">{it.excerpt}</p>}
                <span className="read">{readLabel}</span>
            </div>
        </Link>
    )

    // Bento: gapless image-cover tiles of varied sizes — same look everywhere blog
    // articles appear (full feed + teasers). Rendered by the shared GfBento.
    if (variant === 'bento') {
        return <GfBento items={items.map((it) => ({ ...it, meta: it.date }))} prioritizeText />
    }

    // Rhythm: a full-width hero (newest) + a masonry of the rest.
    if (variant === 'rhythm' && items.length > 1) {
        const [hero, ...rest] = items
        return (
            <>
                <div className="gf-arts lead bl-hero"><Card it={hero} feat /></div>
                <div className="bl-masonry">
                    {rest.map((it) => <Card it={it} key={it.id} />)}
                </div>
            </>
        )
    }

    // Mosaic: 3 sizes — 1 large featured + 2 medium + the rest as small compact cards.
    if (variant === 'mosaic' && items.length > 1) {
        const [feat, ...restAll] = items
        const medium = restAll.slice(0, 2)
        const small = restAll.slice(2)
        return (
            <>
                <div className="gf-arts lead"><Card it={feat} feat /></div>
                {medium.length > 0 && (
                    <div className="gf-arts rest">
                        {medium.map((it) => <Card it={it} key={it.id} />)}
                    </div>
                )}
                {small.length > 0 && (
                    <div className="gf-arts small">
                        {small.map((it) => <Card it={it} key={it.id} />)}
                    </div>
                )}
            </>
        )
    }

    return (
        <div className="gf-arts">
            {items.map((it) => <Card it={it} key={it.id} />)}
        </div>
    )
}
